import { yupResolver } from "@hookform/resolvers/yup";
import AddCircleOutlineOutlined from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTimeoutEffect } from "@react-hookz/web";
import { useCallback, useRef, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { uuidv4 } from "../../../../../utils/uuid";
import { useCachedAssets } from "../../../../assets/hooks/use-cached-assets";
import { useAccountLimitsContext } from "../../../../context/account-limit-context";
import { AutoCompleteSelector } from "../../../../generic/components/auto-complete/auto-complete";
import { useMomentFormSurveys } from "../../../../survey/hooks/use-moment-form-surveys";
import { chatButtonFormSchema } from "../../types/button-list";
import { useSurveysEnabledLexical } from "../hooks/use-surveys-enabled";
import { getSurveyIdFromUrl } from "../utils/survey-url";
import { ListenStatusWrapper } from "./listen-status-wrapper";
import { StyleableButtonUrl } from "./styleable-button-url";

import type { UseSupabaseLoaderState } from "../../../../../server/supabase/hooks/types";
import type { Asset } from "../../../../assets/server/digital-asset-service";
import type { ListenEntitlement } from "../../../../generic/server/supabase-account-service";
import type { TagGroup } from "../../../../merge-tag/components/types";
import type { MomentFormSurvey } from "../../../../survey/server/supabase-survey-service";
import type { ChatButtonForm } from "../../types/button-list";
import type { LinkType } from "./types";
import type { TextFieldProps } from "@mui/material";
import type { FC } from "react";
import type { Control, UseFormGetValues } from "react-hook-form";

const DEFAULT_LINK_TYPE: LinkType = { type: "link_url" };

export const AddButtonListForm: FC<{
  onCancel: () => void;
  onConfirm: (form: ChatButtonForm) => void;
  open: boolean;
  disabled?: boolean;
  filterTags: (x: string) => TagGroup;
  initialState?: ChatButtonForm;
  defaultLinkType?: LinkType;
}> = ({ onCancel, onConfirm, open, disabled, filterTags, defaultLinkType, initialState }) => {
  const { listen_entitlement } = useAccountLimitsContext();
  const assetsLoader = useCachedAssets();
  const surveyLoader = useMomentFormSurveys({ allowedStatuses: ["active"] });

  const methods = useForm<ChatButtonForm>({
    defaultValues: initialState ?? { buttons: [{ id: uuidv4(), text: "", url: "" }] },
    resolver: yupResolver(chatButtonFormSchema),
  });

  const { control, handleSubmit, getValues } = methods;
  const { append, remove } = useFieldArray({ control, name: "buttons" });

  const addButton = useCallback((): void => {
    append({
      id: uuidv4(),
      text: "",
      url: "",
    });
  }, [append]);

  const confirm = useCallback(
    (form: ChatButtonForm) => {
      onConfirm(form);
    },
    [onConfirm],
  );
  useWatch({ control, name: "buttons" });
  const buttons = getValues("buttons") ?? [];

  return (
    <Dialog onClose={onCancel} open={open}>
      <form onSubmit={(e) => void handleSubmit(confirm)(e)}>
        <DialogTitle>Add buttons</DialogTitle>
        {assetsLoader?.loading || surveyLoader?.loading || !listen_entitlement ? (
          <LinearProgress />
        ) : (
          <DialogContent sx={{ p: 1, minWidth: 500 }}>
            {buttons.map((button, index) => (
              <SlackButtonRow
                autoFocus={index === buttons.length - 1}
                key={button.id}
                index={index}
                onRemove={() => remove(index)}
                disabled={disabled}
                filterTags={filterTags}
                assetsLoader={assetsLoader}
                surveyLoader={surveyLoader}
                defaultLinkType={defaultLinkType}
                control={control}
                getValues={getValues}
                listenEntitlement={listen_entitlement}
                onCancel={onCancel}
              />
            ))}
            <Grid size={{ xs: 12 }}>
              <Button
                data-analytics-id="lexical-button-list-dialog-add-button"
                onClick={addButton}
                sx={{ m: 1 }}
                disabled={disabled}
                variant="outlined"
                startIcon={<AddCircleOutlineOutlined />}
              >
                {buttons.length > 0 ? "Add another button" : "Add a button"}
              </Button>
            </Grid>
          </DialogContent>
        )}
        <DialogActions sx={{ pt: 0 }}>
          <Button
            data-analytics-id="lexical-button-list-dialog-cancel"
            type="button"
            onClick={onCancel}
            color="error"
          >
            Cancel
          </Button>
          <Button
            data-analytics-id="lexical-button-list-dialog-add-confirm"
            type="submit"
            color="success"
          >
            Confirm
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const SlackButtonRow: FC<{
  index: number;
  onRemove: () => void;
  disabled?: boolean;
  filterTags: (x: string) => TagGroup;
  control: Control<ChatButtonForm>;
  getValues: UseFormGetValues<ChatButtonForm>;
  autoFocus?: boolean;
  assetsLoader: UseSupabaseLoaderState<Asset[]> & { reload: () => void };
  surveyLoader: UseSupabaseLoaderState<MomentFormSurvey[]>;
  defaultLinkType?: LinkType;
  listenEntitlement: ListenEntitlement;
  onCancel: () => void;
}> = ({
  index,
  onRemove,
  disabled,
  filterTags,
  control,
  getValues,
  autoFocus,
  assetsLoader,
  surveyLoader,
  defaultLinkType = DEFAULT_LINK_TYPE,
  listenEntitlement,
  onCancel,
}) => {
  const surveyjs = useSurveysEnabledLexical();

  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedLinkOption, setSelectedLinkOption] = useState<LinkType>(defaultLinkType);

  useWatch({ control, name: [`buttons.${index}.url`] });
  const url = getValues(`buttons.${index}.url`);

  const survey_id = getSurveyIdFromUrl(url);
  const linkOption: LinkType =
    surveyjs.enabled && survey_id
      ? { type: "link_survey", id: survey_id, channel: surveyjs.type === "channel" }
      : selectedLinkOption;

  useTimeoutEffect(() => autoFocus && inputRef.current?.focus(), 100);

  return (
    <Stack direction="column" spacing={1} sx={{ p: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <RadioGroup
          row
          id="link-option-radio"
          value={linkOption.type}
          onChange={(e) => {
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            setSelectedLinkOption({ type: e.target.value } as LinkType);
          }}
        >
          <FormControlLabel
            key="link-to-url"
            value="link_url"
            control={<Radio />}
            label="Link to URL"
          />
          {surveyjs.enabled && (
            <FormControlLabel
              key="link-to-survey"
              value="link_survey"
              control={<Radio />}
              label="Link to Survey"
            />
          )}
          <FormControlLabel
            key="link-to-content-library"
            value="link_lib"
            control={<Radio />}
            label="Link to Content Library"
          />
        </RadioGroup>
        <Tooltip title="Remove Button" placement="right">
          <span>
            <IconButton
              data-analytics-id="lexical-button-list-delete"
              onClick={onRemove}
              disabled={disabled}
            >
              <DeleteOutline color="error" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <ListenStatusWrapper
        listenEntitlement={listenEntitlement}
        showListenIntent={linkOption.type === "link_survey"}
        surveyLoader={surveyLoader}
        handleClickLearnMore={onCancel}
      >
        <Controller
          control={control}
          name={`buttons.${index}.text`}
          render={({ field, fieldState: { error } }) => (
            <AutoCompleteSelector
              defaultValue={field.value}
              onChange={field.onChange}
              filterTags={filterTags}
              renderInput={(params: TextFieldProps) => (
                <TextField
                  {...params}
                  label="Button Text"
                  placeholder="Button Text"
                  error={Boolean(error)}
                  helperText={error?.message}
                  disabled={disabled}
                  fullWidth
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={`buttons.${index}.url`}
          render={({ field, fieldState: { error } }) => (
            <StyleableButtonUrl
              linkOption={linkOption}
              value={field.value}
              onChange={field.onChange}
              assetsLoader={assetsLoader}
              surveyLoader={surveyLoader}
              error={error}
              filterTags={filterTags}
            />
          )}
        />
      </ListenStatusWrapper>
    </Stack>
  );
};
